import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/css/GuesthouseBooking.css";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const API_BASE = API_ROOT.endsWith("/api") ? API_ROOT : `${API_ROOT}/api`;
const GH_API = `${API_BASE}/guesthouse`;
const CHANGE_REQUEST_ALLOWED_STATUSES = ["Pending", "Cancelled"];

function parseLocalDate(value) {
  if (!value) return null;
  const [y, m, d] = String(value).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function formatApiDate(value) {
  if (!value) return "";
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

const EMPTY_FORM = {
  bookingID: "",
  visitorName: "",
  visitorMobile: "",
  department: "",
  visitorEmail: "",
  address: "",
  stateID: "",
  cityID: "",
  guestHouseID: "",
  roomType: "R",
  visitType: "O",
  noOfRooms: "1",
  relationWithApplicant: "",
  purposeOfVisit: "",
  fromDate: "",
  toDate: "",
  noOfDays: "1",
  recieptNo: "",
  receiptDate: "",
  paymentMode: "P",
  nAmount: "",
  upiID: "",
  jobID: "",
  remarks: ""
};

export default function GuesthouseChangeRequest() {
  const navigate = useNavigate();

  const user = (() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const moduleId = parseInt(user?.moduleId, 10);
  const empId = parseInt(user?.id, 10);

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [guesthouses, setGuesthouses] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [lookedUpStatus, setLookedUpStatus] = useState("");

  useEffect(() => {
    if (![10, 25].includes(moduleId)) {
      navigate("/dashboard", { replace: true });
    }
  }, [moduleId, navigate]);

  useEffect(() => {
    if (![10, 25].includes(moduleId)) return;

    const load = async () => {
      try {
        const [statesRes, ghRes] = await Promise.all([
          fetch(`${API_BASE}/states`),
          fetch(`${GH_API}/options?moduleId=${moduleId}`)
        ]);
        const statesData = await statesRes.json();
        const ghData = await ghRes.json();
        setStates(Array.isArray(statesData) ? statesData : []);
        setGuesthouses(Array.isArray(ghData) ? ghData : []);
      } catch (err) {
        console.error("Failed to load form lookups:", err);
      }
    };
    load();
  }, [moduleId]);

  useEffect(() => {
    if (!formData.stateID) {
      setCities([]);
      return;
    }
    const fetchCities = async () => {
      try {
        const res = await fetch(`${API_BASE}/cities?stateId=${formData.stateID}`);
        const data = await res.json();
        setCities(Array.isArray(data) ? data : []);
      } catch (err) {
        setCities([]);
        console.error("Failed to fetch cities:", err);
      }
    };
    fetchCities();
  }, [formData.stateID]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "bookingID") {
      // Require a fresh eligibility lookup whenever the Booking ID is edited.
      setLookedUpStatus("");
      setError("");
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const applyNoOfRooms = (rawVal) => {
    let val = parseInt(rawVal, 10);
    if (isNaN(val) || val < 1) val = 1;
    setFormData(prev => ({ ...prev, noOfRooms: String(val) }));
  };

  const handleNoOfRoomsChange = (e) => applyNoOfRooms(e.target.value);
  const adjustNoOfRooms = (delta) => applyNoOfRooms((parseInt(formData.noOfRooms, 10) || 1) + delta);

  const handleGuestHouseChange = (e) => {
    setFormData(prev => ({ ...prev, guestHouseID: String(e.target.value), roomType: "R" }));
  };

  const getRoomTypeOptions = () => {
    const ghID = parseInt(formData.guestHouseID, 10);
    const options = [{ value: "R", label: "Room" }];
    if (ghID === 1) {
      options.push({ value: "S", label: "Suite" });
    } else if (ghID === 2) {
      options.push({ value: "C", label: "Conference Hall" });
      options.push({ value: "D", label: "Dining Hall" });
      options.push({ value: "E", label: "Executive Dining Hall" });
      options.push({ value: "A", label: "Conference+Dining+Executive Dining Hall" });
    } else if (ghID === 4) {
      options.push({ value: "V", label: "VC Suite" });
    } else if (ghID === 6) {
      options.push({ value: "H", label: "Seminar Hall" });
    }
    return options;
  };

  const handleDateChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      const from = parseLocalDate(next.fromDate);
      const to = parseLocalDate(next.toDate);
      if (from && to && to >= from) {
        const diffDays = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
        next.noOfDays = String(diffDays > 0 ? diffDays : 1);
      }
      return next;
    });
  };

  const handleBookingIdLookup = async () => {
    const id = formData.bookingID.trim();
    setError("");
    setSuccess("");
    setLookedUpStatus("");
    if (!id) return;

    try {
      setLookupLoading(true);
      const res = await fetch(`${GH_API}/booking/admin-edit/${encodeURIComponent(id)}?moduleId=${moduleId}`);
      const data = await res.json();

      if (!res.ok) {
        window.alert(data.message || "Booking not found.");
        return;
      }

      const b = data.booking || {};
      const inv = data.invoice || {};

      setLookedUpStatus(b.Status || "");
      if (!CHANGE_REQUEST_ALLOWED_STATUSES.includes(b.Status)) {
        setError(`Change Request is only allowed when the existing booking is Pending or Cancelled. Booking ${id} is currently "${b.Status || "Unknown"}".`);
        setFormData({ ...EMPTY_FORM, bookingID: id });
        return;
      }

      setFormData(prev => ({
        ...prev,
        visitorName: b.VisitorName || "",
        visitorMobile: b.Mobile || "",
        department: b.Department || "",
        visitorEmail: b.Email || "",
        address: b.Address || "",
        stateID: b.StateID != null ? String(b.StateID) : "",
        cityID: b.CityID != null ? String(b.CityID) : "",
        guestHouseID: b.GuestHouseID != null ? String(b.GuestHouseID) : "",
        roomType: b.RoomType || "R",
        visitType: b.TypeOfVisit || "O",
        noOfRooms: b.NoOfRooms != null ? String(b.NoOfRooms) : "1",
        relationWithApplicant: b.Relationship || "",
        purposeOfVisit: b.PurposeOfVisit || "",
        fromDate: formatApiDate(b.FromDate),
        toDate: formatApiDate(b.ToDate),
        noOfDays: b.NoOfDays != null ? String(b.NoOfDays) : "1",
        recieptNo: inv.InvoiceNumber || "",
        receiptDate: formatApiDate(b.ReceiptDate),
        paymentMode: b.PaymentMode || "P",
        nAmount: b.Amount != null ? String(b.Amount) : "",
        upiID: b.UPIID || "",
        jobID: b.JobID || "",
        remarks: b.Remarks || ""
      }));
    } catch (err) {
      console.error("Failed to fetch booking:", err);
      window.alert("Failed to fetch booking details.");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.bookingID.trim()) {
      setError("Booking ID is required.");
      return;
    }
    if (!CHANGE_REQUEST_ALLOWED_STATUSES.includes(lookedUpStatus)) {
      setError("Change Request is only allowed when the existing booking is Pending or Cancelled. Please look up an eligible Booking ID first.");
      return;
    }
    if (!formData.guestHouseID) {
      setError("Guest House is required.");
      return;
    }
    if (!formData.fromDate) {
      setError("Check-In Date is required.");
      return;
    }
    if (!formData.toDate) {
      setError("Check-Out Date is required.");
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${GH_API}/booking/change-request/${encodeURIComponent(formData.bookingID.trim())}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          moduleId,
          guestHouseID: formData.guestHouseID,
          fromDate: formData.fromDate,
          toDate: formData.toDate,
          noOfDays: formData.noOfDays,
          roomType: formData.roomType,
          receiptDate: formData.receiptDate,
          paymentMode: formData.paymentMode,
          upiID: formData.upiID,
          visitType: formData.visitType,
          remarks: formData.remarks,
          empId
        })
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Failed to update booking.");
        return;
      }

      setSuccess(data.message || "Booking Request is successfully updated.");
    } catch (err) {
      setError("Error submitting change request.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (![10, 25].includes(moduleId)) return null;

  return (
    <div style={{ textAlign: "left", marginTop: "10px" }}>
      <a
        href="/guesthouse/booking-request-admin"
        className="btn btn-primary"
        style={{ display: "inline-block", padding: "10px 20px", textDecoration: "none" }}
      >
        Booking Request(Admin)
      </a>

      <a
        href="/guesthouse/booking-request-by-mobile"
        className="btn btn-primary"
        style={{ display: "inline-block", padding: "10px 20px", textDecoration: "none" }}
      >
        Booking Request(By Mobile)
      </a>

      <h3 className="ctitle">Change Booking Request</h3>

      <div className="col-lg-10">
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form
          onSubmit={handleSubmit}
          className="guesthouse-booking-form"
          name="FormChangeRequest"
          id="FormChangeRequest"
        >
          <div className="change-request-row-contact">
            <div className="booking-control-item">
              <label className="form-label">Booking ID</label>
              <input
                className="form-control form-control-sm"
                name="bookingID"
                type="text"
                value={formData.bookingID}
                onChange={handleInputChange}
                onBlur={handleBookingIdLookup}
                disabled={lookupLoading}
              />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Names of Visitors</label>
              <input className="form-control form-control-sm" value={formData.visitorName} disabled />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Visitor Mobile</label>
              <input className="form-control form-control-sm" value={formData.visitorMobile} disabled />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Visitor Department</label>
              <input className="form-control form-control-sm" value={formData.department} disabled />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Visitor EMail</label>
              <input className="form-control form-control-sm" value={formData.visitorEmail} disabled />
            </div>
          </div>

          <div className="visitor-location-row">
            <div className="visitor-location-item">
              <label className="form-label">Visitor Permanent Address</label>
              <input className="form-control form-control-sm" value={formData.address} disabled />
            </div>
            <div className="visitor-location-item">
              <label className="form-label">Visitor State</label>
              <select className="form-select form-select-sm" value={formData.stateID} disabled>
                <option value="">Select State</option>
                {states.map(state => (
                  <option key={state.StateID} value={state.StateID}>{state.State}</option>
                ))}
              </select>
            </div>
            <div className="visitor-location-item">
              <label className="form-label">Visitor City</label>
              <select className="form-select form-select-sm" value={formData.cityID} disabled>
                <option value="">Select City</option>
                {cities.map(city => (
                  <option key={city.CityID} value={city.CityID}>{city.City}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="change-request-row-booking">
            <div className="booking-control-item booking-control-guesthouse">
              <label className="form-label">Select Guest House</label>
              <select
                className="form-select form-select-sm"
                name="guestHouseID"
                value={formData.guestHouseID}
                onChange={handleGuestHouseChange}
                required
              >
                <option value="" disabled>Select Guest House</option>
                {guesthouses.map(gh => (
                  <option key={gh.GuestHouseID} value={gh.GuestHouseID}>{gh.GuestHouse}</option>
                ))}
              </select>
            </div>

            <div className="booking-control-item">
              <label className="form-label">Room Type</label>
              <select
                className="form-select form-select-sm"
                name="roomType"
                value={formData.roomType}
                onChange={handleInputChange}
              >
                {getRoomTypeOptions().map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="booking-control-item">
              <label className="form-label">Type of Visit</label>
              <select
                className="form-select form-select-sm"
                name="visitType"
                value={formData.visitType}
                onChange={handleInputChange}
              >
                <option value="O">Official</option>
                <option value="P">Personal</option>
              </select>
            </div>

            <div className="booking-control-item booking-control-rooms">
              <label className="form-label">NoOfRooms</label>
              <div className="rooms-stepper">
                <button type="button" className="rooms-stepper-btn" onClick={() => adjustNoOfRooms(-1)} disabled={parseInt(formData.noOfRooms, 10) <= 1} aria-label="Decrease rooms">−</button>
                <input
                  className="form-control form-control-sm rooms-stepper-input"
                  name="noOfRooms"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  value={formData.noOfRooms}
                  onChange={handleNoOfRoomsChange}
                  onFocus={e => e.target.select()}
                  required
                />
                <button type="button" className="rooms-stepper-btn" onClick={() => adjustNoOfRooms(1)} aria-label="Increase rooms">+</button>
              </div>
            </div>

            <div className="booking-control-item">
              <label className="form-label">Relationship</label>
              <input className="form-control form-control-sm" value={formData.relationWithApplicant} disabled />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Purpose of Visit</label>
              <input className="form-control form-control-sm" value={formData.purposeOfVisit} disabled />
            </div>
          </div>

          <div className="change-request-row-booking">
            <div className="booking-control-item">
              <label className="form-label">Check-In</label>
              <input
                className="form-control form-control-sm"
                type="date"
                value={formData.fromDate}
                onChange={(e) => handleDateChange("fromDate", e.target.value)}
                required
              />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Check-Out</label>
              <input
                className="form-control form-control-sm"
                type="date"
                value={formData.toDate}
                onChange={(e) => handleDateChange("toDate", e.target.value)}
                required
              />
            </div>

            <div className="booking-control-item booking-control-days">
              <label className="form-label">No. Of Days</label>
              <input className="form-control form-control-sm" type="number" value={formData.noOfDays} min="1" readOnly />
            </div>
          </div>

          <div className="change-request-row-payment">
            <div className="booking-control-item">
              <label className="form-label">Rent Reciept No.</label>
              <input className="form-control form-control-sm" value={formData.recieptNo} placeholder="Rent Reciept No...." readOnly />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Reciept Date</label>
              <input
                className="form-control form-control-sm"
                name="receiptDate"
                type="date"
                value={formData.receiptDate}
                onChange={handleInputChange}
              />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Payment Mode</label>
              <select
                className="form-select form-select-sm"
                name="paymentMode"
                value={formData.paymentMode}
                onChange={handleInputChange}
              >
                <option value="P">Pending</option>
                <option value="C">Cash</option>
                <option value="Q">Cheque</option>
                <option value="O">Online</option>
                <option value="F">Free</option>
              </select>
            </div>

            <div className="booking-control-item">
              <label className="form-label">Amount</label>
              <input className="form-control form-control-sm" value={formData.nAmount} placeholder="Amount...." readOnly />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Transaction ID</label>
              <input
                className="form-control form-control-sm"
                name="upiID"
                type="text"
                value={formData.upiID}
                onChange={handleInputChange}
              />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Job ID</label>
              <input
                className="form-control form-control-sm"
                name="jobID"
                type="text"
                value={formData.jobID}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="change-request-row-remarks">
            <div className="booking-control-item">
              <label className="form-label">Remarks</label>
              <input
                className="form-control form-control-sm"
                name="remarks"
                type="text"
                value={formData.remarks}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="col-sm-12">
            <button type="submit" className="btn btn-primary" disabled={loading || !CHANGE_REQUEST_ALLOWED_STATUSES.includes(lookedUpStatus)}>
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
